/**
 * useQuickSearchHotkey - the one keyboard shortcut of the CRM's quick search
 * (docs/plans/crm-report-builder-search-collab-plan.md A2.2, A2.4 and B2.5; decision Q2 a; WP Q-4).
 *
 * One window keydown listener, bubble phase: Ctrl+K, or Cmd+K on a Mac, opens the palette with
 * preventDefault (so the browser's own search box stays shut), from anywhere in the CRM, also from
 * inside a text box. It does nothing at all while an aria-modal="true" dialog is open: the
 * full-screen call workspace is one (components/callmode/CallWorkspace.tsx), and so is every CRM
 * modal and the phone navigation drawer. The palette's own dialog carries QUICK_SEARCH_DIALOG_ATTR
 * and never counts. "/" is never bound: it stays with the recruitment list's grid and is typed
 * constantly in notes (quickSearchLogic isQuickSearchHotkey is the key test).
 *
 * A key another handler already claimed (defaultPrevented) or one typed while an input method is
 * composing is left alone.
 */
import { useEffect, useRef } from 'react';
import { isMacPlatform, isQuickSearchHotkey, shortcutHint } from './quickSearchLogic';

/** The attribute on the palette's own dialog (QuickSearchPalette.tsx), which the modal check skips. */
export const QUICK_SEARCH_DIALOG_ATTR = 'data-quick-search';

/** The fields of a keydown this hook reads (a DOM KeyboardEvent has them all). */
export interface QuickSearchKeyLike {
  key?: string;
  code?: string;
  ctrlKey?: boolean;
  metaKey?: boolean;
  altKey?: boolean;
  shiftKey?: boolean;
  defaultPrevented?: boolean;
  isComposing?: boolean;
}

/**
 * Whether an element is on screen: not inside a `hidden` or inert subtree, not display:none (a
 * stateful panel hides with `hidden` rather than unmounting, so a closed dialog may still be in
 * the DOM), and not visibility:hidden.
 */
function isRendered(el: Element): boolean {
  const view = el.ownerDocument ? el.ownerDocument.defaultView : null;
  for (let node: Element | null = el; node; node = node.parentElement) {
    if (node instanceof HTMLElement && node.hidden) return false;
    if (node.hasAttribute('inert')) return false;
    if (view) {
      const style = view.getComputedStyle(node);
      if (style.display === 'none') return false;
      if (node === el && style.visibility === 'hidden') return false;
    }
  }
  return true;
}

/**
 * True while any aria-modal="true" dialog other than the palette itself is open on the page (the
 * call workspace included). Read at key time, so a dialog that opens or closes later needs no
 * wiring here.
 */
export function isModalDialogOpen(doc: Document = document): boolean {
  const dialogs = Array.from(doc.querySelectorAll('[aria-modal="true"]'));
  return dialogs.some((el) => !el.closest(`[${QUICK_SEARCH_DIALOG_ATTR}]`) && isRendered(el));
}

/** Whether this keydown should open the palette: the shortcut, unclaimed, with no dialog open. */
export function shouldOpenQuickSearch(e: QuickSearchKeyLike, doc: Document = document): boolean {
  if (e.defaultPrevented || e.isComposing) return false;
  if (!isQuickSearchHotkey(e)) return false;
  return !isModalDialogOpen(doc);
}

/** True on a Mac, iPhone or iPad (navigator.platform, else the user agent). */
export function isMacHere(): boolean {
  if (typeof navigator === 'undefined') return false;
  return isMacPlatform(navigator.platform || navigator.userAgent);
}

/** "Ctrl K", or "Cmd K" on a Mac: the hint the trigger shows. */
export function shortcutHintHere(): string {
  return shortcutHint(isMacHere());
}

/** "Ctrl", or "Cmd" on a Mac: the modifier the palette's key hints name. */
export function modifierWordHere(): string {
  return isMacHere() ? 'Cmd' : 'Ctrl';
}

/**
 * Call `onOpen` when Ctrl+K or Cmd+K is pressed anywhere on the page and no dialog is open. The
 * listener is added once (the latest `onOpen` is always the one called) and removed on unmount or
 * while `enabled` is false.
 */
export default function useQuickSearchHotkey(onOpen: () => void, enabled = true): void {
  const onOpenRef = useRef(onOpen);
  useEffect(() => {
    onOpenRef.current = onOpen;
  }, [onOpen]);

  useEffect(() => {
    if (!enabled) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (!shouldOpenQuickSearch(e, document)) return;
      e.preventDefault();
      onOpenRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled]);
}
